/**
 * MKS-162 — task-to-task links API client. Internal-only surface (every
 * backend route uses `authenticate`, never `authenticateAny`).
 *
 *   GET    /api/tasks/:taskId/links
 *   POST   /api/tasks/:taskId/links
 *   DELETE /api/tasks/:taskId/links/:linkId
 */
import { apiGet, apiPost, apiDelete } from './client';

export const TASK_LINK_KINDS = ['parent', 'blocks', 'duplicates', 'relates'] as const;
export type TaskLinkKind = (typeof TASK_LINK_KINDS)[number];

export interface TaskLinkTaskRecord {
  id: number;
  code: string;
  name: string;
  taskStateId: number | null;
  stateName: string | null;
  stateColor: string | null;
  isTerminal: boolean;
}

export interface TaskLinkRecord {
  linkId: number;
  kind: TaskLinkKind;
  direction: 'outgoing' | 'incoming';
  task: TaskLinkTaskRecord;
}

/**
 * GET /api/tasks/:taskId/links
 * Gate: authenticate + requireProjectReadableByTask (admin / PM / project
 * member; open-board internal readers too).
 */
export async function listTaskLinks(taskId: number): Promise<TaskLinkRecord[]> {
  const res = await apiGet<{ data: TaskLinkRecord[] }>(`/tasks/${taskId}/links`);
  return res.data;
}

/**
 * POST /api/tasks/:taskId/links
 * Gate: authenticate + requireAdminOrProjectMemberByTask.
 */
export async function createTaskLink(
  taskId: number,
  input: { toTaskId: number; kind: TaskLinkKind },
): Promise<TaskLinkRecord> {
  const res = await apiPost<{ data: TaskLinkRecord }>(`/tasks/${taskId}/links`, input);
  return res.data;
}

/**
 * DELETE /api/tasks/:taskId/links/:linkId
 * Gate: same as createTaskLink.
 */
export async function deleteTaskLink(taskId: number, linkId: number): Promise<void> {
  await apiDelete(`/tasks/${taskId}/links/${linkId}`);
}

export interface LinkTargetCandidate {
  id: number;
  code: string;
  name: string;
  taskStateId: number | null;
  stateName: string | null;
  isTerminal: boolean;
}

/**
 * GET /api/tasks/search?q=&projectId=&limit=
 *
 * Reuses the EXISTING scoped task-content-search endpoint (MKS-159) rather
 * than building a new server search — it is already shipped, already scoped
 * to what the caller may read, and already returns exactly the shape the
 * link-target picker needs. A client-side filter over board tasks was
 * rejected: the slide-over also mounts on the full-page ticket view and the
 * my-tasks panel, where no board data is in scope, so a client-side filter
 * would leave the picker dead on two of its three mount points.
 *
 * The response puts `nextCursor` as a SIBLING of `data` (not nested under
 * it) — unwrap accordingly, do not reuse the `{ data }`-only unwrap above.
 */
export async function searchLinkTargets(
  projectId: number,
  q: string,
): Promise<LinkTargetCandidate[]> {
  const params = new URLSearchParams({ q, projectId: String(projectId), limit: '10' });
  const res = await apiGet<{ data: LinkTargetCandidate[]; nextCursor: string | null }>(
    `/tasks/search?${params.toString()}`,
  );
  return res.data;
}
